import { BookOpen, Check, CircleAlert, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useBrand } from "@/branding/BrandProvider";
import { formatCalendarDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-admin";

type Schemas = components["schemas"];
export type DemoStepState = NonNullable<Schemas["DemoStepState"]>;
export type DemoPresetKey = NonNullable<Schemas["DemoPreset"]>;

/** What the landing page shows; mapped from GET /demo/status. */
export interface TryView {
  status: NonNullable<Schemas["DemoStatus"]>;
  company: string;
  presetLabel: string | null;
  /** No preset yet (an org made under a Demo client by hand): the prospect picks the business before seeding. */
  needsPreset: boolean;
  startDate: string | null;
  expiresAt: string | null;
  steps: Array<{ key: string; label: string; state: DemoStepState }>;
  error: string | null;
}

/**
 * The page a demo's magic link lands on (spec "AD - Demo Organisations", D13): one click seeds the business and
 * builds the books, with the steps ticking off while it works.
 */
export function TryDolfinBooks({
  view,
  starting,
  onTry,
  preset,
  onPreset,
}: {
  view: TryView;
  starting: boolean;
  onTry: () => void;
  preset?: DemoPresetKey | null;
  onPreset?: (preset: DemoPresetKey) => void;
}) {
  const brand = useBrand();
  const working = view.status === "Seeding" || view.status === "Ready" || starting;
  const picking = view.needsPreset && !working;

  return (
    <main className="relative flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <span className="mb-6 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          {brand.name} demo
        </span>

        <p className="text-[10px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
          {working ? "Setting up" : "Your demo"}
        </p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-balance sm:text-4xl">
          {working ? "Creating your books" : "Try Dolfin Books"}
        </h1>
        <p className="mt-2 text-pretty text-muted-foreground">
          {working
            ? "We're connecting the demo accounts and building twelve months of books. This only takes a moment."
            : "Double-entry books built from your invoices, bills, bank and card payments."}
        </p>

        <section className="mt-6 rounded-xl border bg-card/80 p-5 shadow-sm backdrop-blur-sm" aria-live="polite">
          <div className="flex items-start gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-lg border bg-muted/50 text-muted-foreground">
              <BookOpen className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate font-semibold">{view.company}</p>
              <p className="text-sm text-muted-foreground">
                {view.presetLabel ? `${view.presetLabel} · ` : ""}12 months of invoices, bills, bank and card payments
                {view.startDate ? ` from ${formatCalendarDate(view.startDate, "long")}` : ""}
              </p>
            </div>
          </div>

          {picking ? (
            <div className="mt-5 border-t pt-4">
              <p id="demo-preset-label" className="mb-3 text-sm font-medium">
                Which business would you like to explore?
              </p>
              <div role="radiogroup" aria-labelledby="demo-preset-label" className="grid gap-2 sm:grid-cols-2">
                {(Object.entries(PRESET_LABEL) as Array<[DemoPresetKey, string]>).map(([key, label]) => (
                  <label
                    key={key}
                    className={cn(
                      "cursor-pointer rounded-lg border p-3 transition-colors hover:bg-muted/50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
                      preset === key && "border-primary bg-primary/5 hover:bg-primary/5",
                    )}
                  >
                    <input
                      type="radio"
                      name="demo-preset"
                      value={key}
                      checked={preset === key}
                      onChange={() => onPreset?.(key)}
                      className="sr-only"
                    />
                    <span className="block text-sm font-medium">{label}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{PRESET_DESCRIPTION[key]}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          {working ? (
            <ol className="mt-5 space-y-3 border-t pt-4">
              {view.steps.map((s) => (
                <li key={s.key} className="flex items-center gap-3 text-sm">
                  <StepMark state={starting && view.status === "NotStarted" ? "Pending" : s.state} />
                  <span
                    className={cn(
                      s.state === "Done" && "text-muted-foreground",
                      s.state === "Active" && "font-medium text-foreground",
                      s.state === "Pending" && "text-muted-foreground/70",
                      s.state === "Failed" && "font-medium text-destructive",
                    )}
                  >
                    {s.label}
                  </span>
                </li>
              ))}
            </ol>
          ) : null}

          {view.status === "Failed" ? (
            <div className="mt-5 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm" role="alert">
              <p className="flex items-center gap-1.5 font-medium text-destructive">
                <CircleAlert className="size-4" /> Setting up the demo stopped
              </p>
              {view.error ? <p className="mt-1 text-destructive/90">{view.error}</p> : null}
            </div>
          ) : null}
        </section>

        {view.status === "NotStarted" || view.status === "Failed" ? (
          <Button size="lg" className="mt-6 w-full sm:w-auto" disabled={starting || (picking && !preset)} onClick={onTry}>
            {starting ? <Spinner className="size-4" /> : view.status === "Failed" ? <RotateCcw className="size-4" /> : null}
            {view.status === "Failed" ? "Try again" : "Try Dolfin Books"}
          </Button>
        ) : null}

        <p className="mt-6 text-sm text-muted-foreground">
          <strong className="font-medium text-foreground">This is a demo:</strong> Xero, your bank and Stripe are simulated
          and every figure is made up. Nothing you do here leaves Dolfin
          {view.expiresAt ? `, and it expires on ${formatCalendarDate(view.expiresAt, "long")}` : ""}.
        </p>
      </div>
    </main>
  );
}

function StepMark({ state }: { state: DemoStepState }) {
  if (state === "Done") {
    return (
      <span className="grid size-5 place-items-center rounded-full bg-emerald-500 text-white" aria-label="Done">
        <Check className="size-3" strokeWidth={3} />
      </span>
    );
  }
  if (state === "Failed") {
    return (
      <span className="grid size-5 place-items-center rounded-full bg-destructive text-white" aria-label="Failed">
        <CircleAlert className="size-3" strokeWidth={3} />
      </span>
    );
  }
  if (state === "Active") {
    return (
      <span className="grid size-5 place-items-center" aria-label="In progress">
        <Spinner className="size-4 text-primary" />
      </span>
    );
  }
  return <span className="size-5 rounded-full border-2 border-muted-foreground/25" aria-label="Waiting" />;
}

/** The five steps in order, for before the first status arrives. */
export const DEMO_STEPS: TryView["steps"] = [
  { key: "Accounting", label: "Connecting Xero (demo)", state: "Pending" },
  { key: "Documents", label: "Adding 12 months of invoices, bills and expenses", state: "Pending" },
  { key: "Bank", label: "Connecting your bank (demo)", state: "Pending" },
  { key: "Stripe", label: "Connecting Stripe (demo)", state: "Pending" },
  { key: "Books", label: "Building your books", state: "Pending" },
];

export const PRESET_LABEL: Record<DemoPresetKey, string> = {
  Cafe: "Café",
  Agency: "Agency",
  Ecommerce: "Ecommerce",
  Wholesale: "Wholesale",
};

// One line each, matching what the seeder's templates make.
const PRESET_DESCRIPTION: Record<DemoPresetKey, string> = {
  Cafe: "Daily card takings, catering clients, coffee and produce suppliers",
  Agency: "Retainer clients on terms, freelancers and studio rent",
  Ecommerce: "Online card sales through Stripe, stock, packaging and couriers",
  Wholesale: "Trade customers on 30-day terms, imported stock and haulage",
};
